import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { greeting } from "../portfolio";
import { unlockAudio } from "../utils/soundEngine";

const GLYPHS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!<>-_\\/[]{}—=+*^?#";

function useScramble(target, delay = 300) {
  const [output, setOutput] = useState("");
  const intervalRef = useRef(null);

  useEffect(() => {
    const len = target.length;
    let iteration = 0;

    const timerId = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        setOutput(
          target
            .split("")
            .map((char, i) => {
              if (char === " ") return char;
              if (i < iteration) return char;
              return GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
            })
            .join("")
        );

        iteration += 1 / 3;

        if (iteration >= len) {
          setOutput(target);
          clearInterval(intervalRef.current);
        }
      }, 40);
    }, delay);

    return () => {
      clearTimeout(timerId);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [target, delay]);

  return output || "\u00A0".repeat(target.length);
}

function useCounter(target, duration = 2000, delay = 100) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime;
    let frameId;

    const timerId = setTimeout(() => {
      const animate = (now) => {
        if (!startTime) startTime = now;
        const progress = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        setCount(Math.floor(target * eased));
        if (progress < 1) frameId = requestAnimationFrame(animate);
      };
      frameId = requestAnimationFrame(animate);
    }, delay);

    return () => {
      clearTimeout(timerId);
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [target, duration, delay]);

  return count;
}

function LoadingScreen() {
  const scrambled = useScramble(greeting.logo_name, 300);
  const count = useCounter(100, 1850, 100);

  return (
    <motion.div
      onPointerDown={() => unlockAudio()}
      exit={{ clipPath: "inset(0% 0% 100% 0%)" }}
      transition={{ duration: 0.75, ease: [0.76, 0, 0.24, 1] }}
      style={{ clipPath: "inset(0% 0% 0% 0%)" }}
      className="fixed inset-0 z-[100] bg-bg flex items-center justify-center select-none"
    >
      <div className="flex flex-col items-center gap-10">
        {/* Counter */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05, duration: 0.4 }}
        >
          <span className="font-mono text-7xl sm:text-8xl font-extralight text-fg-ghost tracking-tighter tabular-nums">
            {count}
          </span>
        </motion.div>

        {/* Scramble text */}
        <div className="overflow-hidden">
          <motion.span
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{
              duration: 0.5,
              delay: 0.15,
              ease: [0.16, 1, 0.3, 1],
            }}
            className="block font-mono text-base sm:text-lg text-fg-muted tracking-[0.15em]"
          >
            {scrambled}
          </motion.span>
        </div>

        {/* Progress line */}
        <motion.div
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{
            scaleX: { duration: 1.85, delay: 0.15, ease: [0.16, 1, 0.3, 1] },
            opacity: { duration: 0.3, delay: 0.15 },
          }}
          className="w-24 h-px bg-fg/15 origin-left"
        />

        {/* Subtitle */}
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.6 }}
          className="text-[10px] font-mono text-fg-ghost tracking-[0.3em] uppercase"
        >
          Loading
        </motion.span>
      </div>
    </motion.div>
  );
}

export default LoadingScreen;
